import type { ServiceConsentInput, ServiceTermsResponse, ServiceContractsResponse, ServiceWithdrawalInput, ServiceWithdrawalResponse } from "@/lib/service-contracts";
import type { AccountPlan, CheckoutStatusResponse, SubscriptionAccountResponse, SubscriptionCatalog, SubscriptionCurrency, SubscriptionScalesResponse } from "@/lib/subscriptions";

const API_URL = import.meta.env.VITE_API_URL;

export const GOOGLE_CLIENT_ID = "316738848411-60o856b8q0jrfbtc5u3qho5hiirrsvsn.apps.googleusercontent.com";

export interface AuthUser {
  id: string;
  name: string;
  lang: string;
}

interface JWTPayload {
  id: string;
  name: string;
  lang: string;
  exp: number;
}

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad) base64 += "=".repeat(4 - pad);
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export function getUserFromToken(token: string): AuthUser | null {
  if (isTokenExpired(token)) return null;
  const payload = decodeJWT(token);
  if (!payload) return null;
  return { id: payload.id, name: payload.name, lang: payload.lang };
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

// --- API helpers ---

export async function apiLogin(email: string, password: string) {
  const res = await fetch(`${API_URL}/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return res.json();
}

export async function apiRegisterProvideEmail(email: string) {
  const res = await fetch(`${API_URL}/register/provide-email/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function apiRegisterCheckCode(email: string, code: string) {
  const res = await fetch(`${API_URL}/register/check-code/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  return res.json();
}

export async function apiRegisterConfirm(data: {
  email: string;
  password: string;
  code: string;
  name: string;
  countryCode: string;
  language: string;
}) {
  const res = await fetch(`${API_URL}/register/confirm/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function apiGmailLogin(token: string, countryCode: string, language: string) {
  const res = await fetch(`${API_URL}/login/gmail/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, countryCode, language }),
  });
  return res.json();
}

export async function apiResetPassword(email: string) {
  const res = await fetch(`${API_URL}/login/reset-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function apiResetPasswordCheckCode(email: string, code: string) {
  const res = await fetch(`${API_URL}/login/reset-password/check-code/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  return res.json();
}

export async function apiResetPasswordConfirm(email: string, password: string, code: string) {
  const res = await fetch(`${API_URL}/login/reset-password/confirm/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, code }),
  });
  return res.json();
}

export async function apiUpdateAccount(token: string, name: string, language: string) {
  const res = await fetch(`${API_URL}/account/update/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name, language }),
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiChangePassword(token: string, password: string) {
  const res = await fetch(`${API_URL}/account/new-password/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiGetDashboard(token: string) {
  const res = await fetch(`${API_URL}/api/web/account/dashboard/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiGetOrders(token: string) {
  const res = await fetch(`${API_URL}/api/web/account/orders/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiGetProducts(token: string) {
  const res = await fetch(`${API_URL}/api/web/account/products/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiGetProductDetail(token: string, id: string) {
  const res = await fetch(`${API_URL}/api/web/account/products/${id}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiUpdateProductName(token: string, id: string, name: string) {
  const res = await fetch(`${API_URL}/api/web/account/products/${id}/update-name/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export async function apiTransferProduct(token: string, productId: string, email: string) {
  const res = await fetch(`${API_URL}/api/web/account/products/transfer/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ product_id: productId, email }),
  });
  if (res.status === 401) return { status: "unauthorized" };
  return res.json();
}

export class SubscriptionApiError extends Error {
  constructor(public status: string) { super(status); }
}

async function subscriptionRequest<T>(path: string, token?: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_URL}/api/web/subscriptions/${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 401) throw new SubscriptionApiError("unauthorized");
  const data = await response.json();
  if (!response.ok || data.status !== "ok") throw new SubscriptionApiError(typeof data.status === "string" ? data.status : "request_failed");
  return data as T;
}

const withQuery = (path: string, params: Record<string, string | undefined>) => {
  const query = new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => !!entry[1])).toString();
  return query ? `${path}?${query}` : path;
};
// Amounts are always quoted by the server; the client only chooses the currency.
export const apiSubscriptionCatalog = (currency: SubscriptionCurrency = "PLN") =>
  subscriptionRequest<SubscriptionCatalog>(withQuery("catalog/", { currency }));
export const apiSubscriptionAccount = (token: string, apiaryId?: string, currency: SubscriptionCurrency = "PLN") =>
  subscriptionRequest<SubscriptionAccountResponse>(withQuery("account/", { apiary_id: apiaryId, currency }), token);
export const apiSubscriptionCheckout = (token: string, plan: AccountPlan, consents: ServiceConsentInput, currency: SubscriptionCurrency = "PLN") =>
  subscriptionRequest<{ status: "ok"; url: string }>("checkout/", token, { plan, consents, currency });
export const apiSubscriptionPortal = (token: string) =>
  subscriptionRequest<{ status: "ok"; url: string }>("portal/", token, {});
export const apiSubscriptionCheckoutStatus = (token: string, sessionId: string) =>
  subscriptionRequest<CheckoutStatusResponse>(`checkout-status/?session_id=${encodeURIComponent(sessionId)}`, token);
export const apiSubscriptionCheckoutCancel = (token: string, quoteKey: string) =>
  subscriptionRequest<{ status: "ok" }>("checkout-cancel/", token, { quote_key: quoteKey });
export const apiSubscriptionScales = (token: string, currency: SubscriptionCurrency = "PLN") =>
  subscriptionRequest<SubscriptionScalesResponse>(withQuery("scales/", { currency }), token);
export const apiSubscriptionScalesCheckout = (token: string, productIds: string[], consents: ServiceConsentInput, currency: SubscriptionCurrency = "PLN") =>
  subscriptionRequest<{ status: "ok"; url: string }>("scales/checkout/", token, { product_ids: productIds, consents, currency });

export const apiServiceTerms = (language: string) => subscriptionRequest<ServiceTermsResponse>(`terms/?language=${encodeURIComponent(language)}`);
export const apiServiceContracts = (token: string, page = 1) => subscriptionRequest<ServiceContractsResponse>(`contracts/?page=${page}`, token);
export const apiServiceWithdrawal = (token: string, input: ServiceWithdrawalInput) => subscriptionRequest<ServiceWithdrawalResponse>("withdrawals/", token, input);
